import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Invoice } from '../../api/types.js';

// REQ-FE-029a-2 — Keyboard hook for the split-pane invoice list:
//   j / k         → move the selected row down / up; updates URL
//                   (`/invoices/:primaryKey`) via `navigate` so the
//                   centre-pane InvoiceDetail re-renders on the new pk.
//   d             → trigger the "Soumettre un litige" action button on
//                   the currently selected detail.
//   v             → trigger the "Demander une validation" action button.
//   a             → trigger the "Assigner" action button.
//   ?             → no-op today; reserved for the help modal.
//
// e (accuser réception), p (traiter), r (rejeter) are listed in the
// mockup footer strip but have no live UI action behind them yet —
// they're wired here as no-ops so the strip stays mockup-faithful
// while we wait for the corresponding actions to ship.
//
// Letter-key dispatch uses `document.querySelector('[data-testid=...]')
// .click()` rather than threading callbacks through props. Rationale:
// the action handlers live deep inside `DetailBody`'s local state
// (`useDisputeInvoice`, `useRequestValidation`, etc.); hoisting them
// up to InvoiceListScreen would mean lifting 3+ mutations and a half
// dozen useState flags. The querySelector path is small and surgical
// — when the orchestrator says it's the wrong call we can swap to a
// `useImperativeHandle` ref bridge in <30 lines.
//
// Hook is a no-op when `enabled` is false (single-pane fallback at
// <1280px viewport).

const ACTION_KEY_MAP: Record<string, string> = {
  d: 'action-dispute',
  v: 'action-validation',
  a: 'action-assign',
};

interface Options {
  enabled: boolean;
  items: Invoice[];
  selectedPrimaryKey: string | undefined;
  tenantId: string;
}

function isInsideEditableField(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

export function useInvoiceListKeyboard({
  enabled,
  items,
  selectedPrimaryKey,
  tenantId,
}: Options): void {
  const navigate = useNavigate();

  useEffect(() => {
    if (!enabled) return;
    function onKey(e: KeyboardEvent) {
      // Skip when focus is in a form field — prevents stealing keystrokes
      // from the comments composer, dispute form, etc.
      if (isInsideEditableField(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const key = e.key.toLowerCase();

      // j/k navigation: walk the row list. Wrap-around so power users
      // can keep pressing j to scan an inbox.
      if (key === 'j' || key === 'k') {
        if (items.length === 0) return;
        e.preventDefault();
        const currentIdx = selectedPrimaryKey
          ? items.findIndex((i) => i.primaryKey === selectedPrimaryKey)
          : -1;
        const len = items.length;
        let nextIdx: number;
        if (key === 'j') {
          nextIdx = currentIdx < 0 ? 0 : (currentIdx + 1) % len;
        } else {
          nextIdx = currentIdx <= 0 ? len - 1 : currentIdx - 1;
        }
        const next = items[nextIdx];
        if (next) {
          navigate(`/t/${tenantId}/invoices/${encodeURIComponent(next.primaryKey)}`, {
            replace: true,
          });
        }
        return;
      }

      // Letter-key action dispatch: only when a row is selected (the
      // action buttons live inside the centre pane's InvoiceDetail).
      if (!selectedPrimaryKey) return;
      const testid = ACTION_KEY_MAP[key];
      if (!testid) return;
      const btn = document.querySelector<HTMLButtonElement>(`[data-testid="${testid}"]`);
      if (btn && !btn.disabled) {
        e.preventDefault();
        btn.click();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, items, selectedPrimaryKey, tenantId, navigate]);
}

// Export for tests that want to assert against the mapping.
export const _INTERNAL_ACTION_KEY_MAP = ACTION_KEY_MAP;
